import { useState } from "react";
// form
import * as Yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
} from "@mui/material";
// @mui
import { Stack, IconButton, InputAdornment } from "@mui/material";
import { LoadingButton } from "@mui/lab";
// components
import Iconify from "../../components/Iconify";
import { FormProvider, RHFTextField } from "../../components/hook-form";
// locale
// redux
import { loginUserAsync } from "../../app/services/auth/authSlice";
import { useAppDispatch } from "../../app/hooks";
// ----------------------------------------------------------------------

export function LoginForm() {
  // redux
  const dispatch = useAppDispatch();
  const [value, setValue] = useState("employee");

  // Add useEffect import at the top if not already present
  const handleFormValidation = () => {
    if (value === "employee") {
      trigger("email");
      trigger("password");
    } else {
      trigger("mobile");
      trigger("password");
    }
  };

  const [showPassword, setShowPassword] = useState(false);

  const LoginSchema = Yup.object().shape({
    email: value === "employee"
      ? Yup.string()
        .email("Email is invalid")
        .required("Email required")
      : Yup.string(),
    mobile: value === "agent"
      ? Yup.string()
        .matches(/^[0-9]{10}$/, "Mobile number must be 10 digits")
        .required("Mobile number is required")
      : Yup.string(),
    password: Yup.string()
      .when(['email', 'Mobile'], {
        is: (email: string, mobile: string) => {
          return value === "employee" ? !!email : !!mobile;
        },
        then: (schema) => schema.required("Please enter password"),
        otherwise: (schema) => schema.notRequired(),
      }),
  });
  const handleChangeRadio = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = (event.target as HTMLInputElement).value;
    setValue(newValue);
    // Reset form validation on type change
    setTimeout(() => {
      handleFormValidation();
    }, 0);
  };

  const defaultValues = {
    email: "",
    mobile: "",
    password: "",
    login_type: "email"
  };

  const methods = useForm({
    resolver: yupResolver(LoginSchema),
    defaultValues,
    mode: "all",
    reValidateMode: "onChange",
    criteriaMode: "all",
  });

  const {
    handleSubmit,
    formState: { isSubmitting, errors, isValid, isDirty, touchedFields },
    trigger,
  } = methods;

  const onSubmit = async (data: typeof defaultValues) => {
    const loginData = value === "employee"
      ? {
        email: data.email,
        password: data.password,
        login_type: "email"
      }
      : {
        mobile: data.mobile,
        password: data.password,
        login_type: "mobile"
      };
    //@ts-ignore
    dispatch(loginUserAsync(loginData));
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3}>
        <FormControl component="fieldset" style={{ display: "inline" }}>
          <RadioGroup
            name="user"
            style={{ display: "inline" }}
            value={value}
            onChange={handleChangeRadio}
          >
            <FormControlLabel
              value="employee"
              control={<Radio />}
              label="Employee"
            />
            <FormControlLabel value="agent" control={<Radio />} label="Agent" />
          </RadioGroup>
        </FormControl>
        {value === "employee" && (
          <RHFTextField
            name="email"
            label="Email"
            type="text"
          />
        )}
        {value === "agent" && (
          <RHFTextField
            name="mobile"
            label="Mobile Number"
            type="text"
          />
        )}
        <RHFTextField
          name="password"
          label="Password"
          type={showPassword ? "text" : "password"}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                >
                  <Iconify
                    icon={showPassword ? "eva:eye-fill" : "eva:eye-off-fill"}
                  />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Stack>
      <br />
      <LoadingButton
        fullWidth
        size="large"
        type="submit"
        variant="contained"
        loading={isSubmitting}
        disabled={!isValid || !isDirty || isSubmitting}
      >
        Login
      </LoadingButton>
    </FormProvider>
  );
}
